// FreshPantryLayout.js - Grocery Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-green-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 dark:border-green-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function FreshPantryLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#16a34a";
  const secondary = T.secondaryColor || "#f59e0b";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Grocery hero images - DIRECTLY FROM CONFIG
  const groceryHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ef6e8dd3-2b25-44db-85ae-5f09c9a90a97_g1.jpg",
    },
    {
      field: "groceryHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3577acf3-fd5f-4c39-88a6-fc981880abed_g2.jpg",
    },
    {
      field: "groceryHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/52a3d7f9-77ce-4a9f-8be6-fba98c65cc87_g3.jpg",
    },
    {
      field: "groceryHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c4fd9b12-8282-4457-a364-f716cbd89c5c_g4.jpg",
    },
    {
      field: "groceryHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/03ca24d5-701e-4942-a179-3b88ef42becd_g5.jpg",
    },
  ];

  // Grocery Collection Data - Added imageField
  const groceryCollection = [
    // Packaged Foods
    {
      id: 1,
      nameKey: "item1_name",
      name: "Basmati Rice (5kg)",
      priceKey: "item1_price",
      price: 399,
      originalPriceKey: "item1_original_price",
      originalPrice: 549,
      imageField: "item1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b33fdb5c-6b1b-4613-8011-21f49b919d83_basamati.jpg",
      categoryKey: "item1_category",
      category: "Packaged Foods",
      subCategory: "Rice & Grains",
      descriptionKey: "item1_description",
      description: "Premium quality aged basmati rice with 2-year shelf life.",
      badge: "Bestseller",
      badgeColor: "#16a34a",
      weight: "5kg",
      brand: "Golden Grain",
      inStock: true,
    },
    {
      id: 2,
      nameKey: "item2_name",
      name: "Wheat Flour (Atta) 10kg",
      priceKey: "item2_price",
      price: 299,
      originalPriceKey: "item2_original_price",
      originalPrice: 399,
      imageField: "item2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/b8d964e4-0e56-4bde-aba4-8288af4f503d_Wheat_Flour_(Atta)_10kg.jpg",
      categoryKey: "item2_category",
      category: "Packaged Foods",
      subCategory: "Flour & Grains",
      descriptionKey: "item2_description",
      description: "Stone-ground whole wheat flour for perfect chapatis.",
      badge: "Value Pack",
      badgeColor: "#f59e0b",
      weight: "10kg",
      brand: "FreshMills",
      inStock: true,
    },
    {
      id: 3,
      nameKey: "item3_name",
      name: "Pasta (500g)",
      priceKey: "item3_price",
      price: 89,
      originalPriceKey: "item3_original_price",
      originalPrice: 129,
      imageField: "item3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/51e587e4-4c61-4973-ba38-ab33d43f0d7b_pasta.jpg",
      categoryKey: "item3_category",
      category: "Packaged Foods",
      subCategory: "Pasta & Noodles",
      descriptionKey: "item3_description",
      description: "Italian-style durum wheat pasta, perfect for any sauce.",
      badge: "Popular",
      badgeColor: "#dc2626",
      weight: "500g",
      brand: "Italiano",
      inStock: true,
    },
    {
      id: 4,
      nameKey: "item4_name",
      name: "Breakfast Cereal (1kg)",
      priceKey: "item4_price",
      price: 249,
      originalPriceKey: "item4_original_price",
      originalPrice: 349,
      imageField: "item4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/40fb570b-4b45-481c-9a6a-d3c8213a5f3a_Breakfast_Cereal_(1kg).webp",
      categoryKey: "item4_category",
      category: "Packaged Foods",
      subCategory: "Breakfast",
      descriptionKey: "item4_description",
      description: "Crunchy cornflakes with added vitamins and minerals.",
      badge: "Family Pack",
      badgeColor: "#8b5cf6",
      weight: "1kg",
      brand: "MorningFresh",
      inStock: true,
    },
    
    // Spices & Masalas
    {
      id: 5,
      nameKey: "item5_name",
      name: "Premium Turmeric Powder",
      priceKey: "item5_price",
      price: 99,
      originalPriceKey: "item5_original_price",
      originalPrice: 149,
      imageField: "item5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5e216410-2ba5-41ea-9017-9ddad41920c4_Premium_Turmeric_Powder.jpg",
      categoryKey: "item5_category",
      category: "Spices & Masalas",
      subCategory: "Ground Spices",
      descriptionKey: "item5_description",
      description: "Pure turmeric powder with high curcumin content.",
      badge: "Premium",
      badgeColor: "#7c3aed",
      weight: "200g",
      brand: "SpiceMaster",
      inStock: true,
    },
    {
      id: 6,
      nameKey: "item6_name",
      name: "Garam Masala",
      priceKey: "item6_price",
      price: 79,
      originalPriceKey: "item6_original_price",
      originalPrice: 119,
      imageField: "item6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5ad33295-2cef-4a0c-b858-1ca8a46d6e24_garammasala.jpg",
      categoryKey: "item6_category",
      category: "Spices & Masalas",
      subCategory: "Blended Masalas",
      descriptionKey: "item6_description",
      description: "Authentic garam masala blend with 12 aromatic spices.",
      badge: "Signature",
      badgeColor: "#d97706",
      weight: "100g",
      brand: "SpiceMaster",
      inStock: true,
    },
    {
      id: 7,
      nameKey: "item7_name",
      name: "Chili Powder",
      priceKey: "item7_price",
      price: 69,
      originalPriceKey: "item7_original_price",
      originalPrice: 99,
      imageField: "item7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/14de8d70-6c82-416a-bf91-743bce4382eb_Chili_Powder.jpg",
      categoryKey: "item7_category",
      category: "Spices & Masalas",
      subCategory: "Ground Spices",
      descriptionKey: "item7_description",
      description: "Premium quality chili powder with vibrant color.",
      badge: "Hot",
      badgeColor: "#dc2626",
      weight: "100g",
      brand: "SpiceMaster",
      inStock: true,
    },
    {
      id: 8,
      nameKey: "item8_name",
      name: "Cumin Seeds (Jeera)",
      priceKey: "item8_price",
      price: 59,
      originalPriceKey: "item8_original_price",
      originalPrice: 89,
      imageField: "item8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ca1f6fb8-5f6c-48d8-88d9-6a7cad528d0e_Cumin_Seeds_(Jeera).jpg",
      categoryKey: "item8_category",
      category: "Spices & Masalas",
      subCategory: "Whole Spices",
      descriptionKey: "item8_description",
      description: "Aromatic cumin seeds for tempering and cooking.",
      badge: "Essential",
      badgeColor: "#059669",
      weight: "200g",
      brand: "SpiceMaster",
      inStock: true,
    },
    
    // Oils & Ghee
    {
      id: 9,
      nameKey: "item9_name",
      name: "Pure Mustard Oil (1L)",
      priceKey: "item9_price",
      price: 199,
      originalPriceKey: "item9_original_price",
      originalPrice: 279,
      imageField: "item9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/fc4958a8-fd43-48f5-9632-d7611c9ee445_Pure_Mustard_Oil_(1L).jpg",
      categoryKey: "item9_category",
      category: "Oils & Ghee",
      subCategory: "Cooking Oils",
      descriptionKey: "item9_description",
      description: "Cold-pressed mustard oil for authentic Indian cooking.",
      badge: "Pure",
      badgeColor: "#059669",
      weight: "1L",
      brand: "GoldenDrop",
      inStock: true,
    },
    {
      id: 10,
      nameKey: "item10_name",
      name: "Ghee (500ml)",
      priceKey: "item10_price",
      price: 299,
      originalPriceKey: "item10_original_price",
      originalPrice: 399,
      imageField: "item10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/dce9fbf7-72b3-47c4-aad4-21522696a06e_Ghee.jpg",
      categoryKey: "item10_category",
      category: "Oils & Ghee",
      subCategory: "Ghee",
      descriptionKey: "item10_description",
      description: "Premium quality pure cow ghee for cooking and wellness.",
      badge: "Premium",
      badgeColor: "#f59e0b",
      weight: "500ml",
      brand: "PureClarified",
      inStock: true,
    },
    
    // Snacks & Beverages
    {
      id: 11,
      nameKey: "item11_name",
      name: "Tea (250g)",
      priceKey: "item11_price",
      price: 149,
      originalPriceKey: "item11_original_price",
      originalPrice: 199,
      imageField: "item11_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/099b7d40-f3b3-4b2e-9651-005ea8d6a3a4_Tea.jpg",
      categoryKey: "item11_category",
      category: "Beverages",
      subCategory: "Tea & Coffee",
      descriptionKey: "item11_description",
      description: "Premium Assam tea leaves for a perfect cup.",
      badge: "Best Brew",
      badgeColor: "#8b5cf6",
      weight: "250g",
      brand: "TeaLeaf",
      inStock: true,
    },
    {
      id: 12,
      nameKey: "item12_name",
      name: "Instant Coffee (100g)",
      priceKey: "item12_price",
      price: 199,
      originalPriceKey: "item12_original_price",
      originalPrice: 279,
      imageField: "item12_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/201e6334-2018-4034-9de8-eb868df9faf0_Instant_Coffee.jpg",
      categoryKey: "item12_category",
      category: "Beverages",
      subCategory: "Tea & Coffee",
      descriptionKey: "item12_description",
      description: "Rich instant coffee for your daily caffeine fix.",
      badge: "Bestseller",
      badgeColor: "#d97706",
      weight: "100g",
      brand: "CoffeeBean",
      inStock: true,
    },
    {
      id: 13,
      nameKey: "item13_name",
      name: "Biscuit Pack (500g)",
      priceKey: "item13_price",
      price: 89,
      originalPriceKey: "item13_original_price",
      originalPrice: 129,
      imageField: "item13_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d6a5ad60-f587-40d5-b6e2-3e178aea9210_Biscuit_Pack.jpg",
      categoryKey: "item13_category",
      category: "Snacks",
      subCategory: "Cookies & Biscuits",
      descriptionKey: "item13_description",
      description: "Classic butter biscuits, perfect for tea time.",
      badge: "Tea Time",
      badgeColor: "#f59e0b",
      weight: "500g",
      brand: "BakeHouse",
      inStock: true,
    },
    {
      id: 14,
      nameKey: "item14_name",
      name: "Namkeen Mix (400g)",
      priceKey: "item14_price",
      price: 69,
      originalPriceKey: "item14_original_price",
      originalPrice: 99,
      imageField: "item14_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/f469d245-5662-4b5c-ae6d-f0ef8c6eae09_Namkeen_Mix.jpg",
      categoryKey: "item14_category",
      category: "Snacks",
      subCategory: "Savory Snacks",
      descriptionKey: "item14_description",
      description: "Traditional Indian namkeen mix with spices and nuts.",
      badge: "Crunchy",
      badgeColor: "#dc2626",
      weight: "400g",
      brand: "CrunchTime",
      inStock: true,
    },
    
    // Pulses & Grains
    {
      id: 15,
      nameKey: "item15_name",
      name: "Toor Dal (1kg)",
      priceKey: "item15_price",
      price: 179,
      originalPriceKey: "item15_original_price",
      originalPrice: 249,
      imageField: "item15_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1adf25e3-751c-4bae-9610-89b83f45bafc_Toor_Dal.jpg",
      categoryKey: "item15_category",
      category: "Pulses & Legumes",
      subCategory: "Dals",
      descriptionKey: "item15_description",
      description: "Premium quality toor dal (pigeon peas) for traditional cooking.",
      badge: "Essential",
      badgeColor: "#16a34a",
      weight: "1kg",
      brand: "PulseFresh",
      inStock: true,
    },
    {
      id: 16,
      nameKey: "item16_name",
      name: "Moong Dal (500g)",
      priceKey: "item16_price",
      price: 99,
      originalPriceKey: "item16_original_price",
      originalPrice: 149,
      imageField: "item16_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7def0937-20d5-47d9-bb61-1290f0cf5ba7_Moong_Dal.webp",
      categoryKey: "item16_category",
      category: "Pulses & Legumes",
      subCategory: "Dals",
      descriptionKey: "item16_description",
      description: "Split moong dal, perfect for khichdi and soups.",
      badge: "Easy Cook",
      badgeColor: "#8b5cf6",
      weight: "500g",
      brand: "PulseFresh",
      inStock: true,
    },
  ];

  // Categories
  const groceryCategories = [
    { key: "all", icon: "🛒", label: "All Items", color: "#16a34a" },
    { key: "packaged-foods", icon: "📦", label: "Packaged Foods", color: "#2563eb" },
    { key: "spices", icon: "🌶️", label: "Spices & Masalas", color: "#dc2626" },
    { key: "oils-ghee", icon: "🫒", label: "Oils & Ghee", color: "#f59e0b" },
    { key: "pulses", icon: "🫘", label: "Pulses & Legumes", color: "#059669" },
    { key: "snacks", icon: "🍿", label: "Snacks", color: "#8b5cf6" },
    { key: "beverages", icon: "☕", label: "Beverages", color: "#d97706" },
  ];

  // Offers & Deals - Added imageField
  const offers = [
    {
      id: 1,
      titleKey: "offer1_title",
      title: "Buy 2 Get 1 Free",
      descKey: "offer1_desc",
      desc: "On all packaged foods",
      imageField: "offer1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3577acf3-fd5f-4c39-88a6-fc981880abed_g2.jpg",
      color: "#dc2626",
      badge: "🔥 Limited Time"
    },
    {
      id: 2,
      titleKey: "offer2_title",
      title: "20% Off on Spices",
      descKey: "offer2_desc",
      desc: "Premium quality masalas",
      imageField: "offer2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5ad33295-2cef-4a0c-b858-1ca8a46d6e24_garammasala.jpg",
      color: "#f59e0b",
      badge: "🎉 Flash Sale"
    },
    {
      id: 3,
      titleKey: "offer3_title",
      title: "Combo Deal",
      descKey: "offer3_desc",
      desc: "Oil + Ghee at special price",
      imageField: "offer3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/fc4958a8-fd43-48f5-9632-d7611c9ee445_Pure_Mustard_Oil_(1L).jpg",
      color: "#16a34a",
      badge: "💪 Save 30%"
    },
  ];

  // Brands
  const brands = [
    { id: 1, name: "Golden Grain", logo: "🌾", color: "#f59e0b" },
    { id: 2, name: "SpiceMaster", logo: "🌶️", color: "#dc2626" },
    { id: 3, name: "FreshMills", logo: "🏭", color: "#16a34a" },
    { id: 4, name: "PulseFresh", logo: "🫘", color: "#8b5cf6" },
    { id: 5, name: "TeaLeaf", logo: "🍃", color: "#059669" },
    { id: 6, name: "GoldenDrop", logo: "🫒", color: "#d97706" },
  ];

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Spice Guide",
      descKey: "post1_desc",
      desc: "Understanding Indian spices and their uses.",
      fullContentKey: "post1_content",
      fullContent: "Indian cuisine is defined by its rich tapestry of spices. From the warmth of cinnamon to the heat of chili, each spice brings its own unique character to the dish. Our guide helps you understand which spices to use and when.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5ad33295-2cef-4a0c-b858-1ca8a46d6e24_garammasala.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Chef Anand",
      category: "Cooking Tips"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Healthy Eating",
      descKey: "post2_desc",
      desc: "Tips for a balanced diet with pantry staples.",
      fullContentKey: "post2_content",
      fullContent: "A well-stocked pantry is the first step to healthy eating. From whole grains to legumes and healthy oils, we share tips on building a nutritious and balanced diet with everyday ingredients.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3577acf3-fd5f-4c39-88a6-fc981880abed_g2.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Dr. Meera Sharma",
      category: "Health & Nutrition"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Storage Tips",
      descKey: "post3_desc",
      desc: "How to store groceries for maximum freshness.",
      fullContentKey: "post3_content",
      fullContent: "Proper storage is key to reducing waste and maintaining freshness. Learn the best ways to store different types of groceries, from grains and spices to oils and packaged foods.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/e01ccce8-c208-4d2e-92f6-709170c57ac4_Storage_Tips.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Ravi Kumar",
      category: "Kitchen Tips"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_shop", label: "SHOP", page: "shop" },
    { key: "nav_spices", label: "SPICES", page: "spices" },
    { key: "nav_offers", label: "OFFERS", page: "offers" },
    { key: "nav_brands", label: "BRANDS", page: "brands" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🛒 FREE DELIVERY ON FIRST ORDER",
    "✦ USE CODE 'FRESH10' FOR 10% OFF",
    "📦 MINIMUM ORDER ₹299",
    "✦ 7-DAY EASY RETURNS"
  ];

  // Floating particles (grocery bag/items)
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 3 + Math.random() * 6,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.1 + Math.random() * 0.2,
  }));

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % groceryHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openOfferModal = useCallback((offer, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedOffer(offer);
  }, [p.previewEditMode, p.activeEditField]);

  const closeOfferModal = useCallback(() => {
    setSelectedOffer(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedItem(null);
      setSelectedPost(null);
      setSelectedOffer(null);
      setSelectedBrand(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || groceryHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = groceryHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, groceryHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "FRESH PANTRY",
    title: p.storeTheme?.heroTitle || "Everything for Your Kitchen",
    subtitle: p.storeTheme?.heroSubtitle || "Quality groceries delivered to your doorstep",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Offers",
  };

  const isDarkTheme = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDark = isDarkTheme(T.backgroundColor);

  // Get editable items with imageField
  const editableGroceryCollection = groceryCollection.map((item) => ({
    ...item,
    image: p.storeTheme?.[item.imageField] || item.image,
  }));

  // Get editable offers with imageField
  const editableOffers = offers.map((offer) => ({
    ...offer,
    image: p.storeTheme?.[offer.imageField] || offer.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableGroceryCollection;
    const map = {
      "packaged-foods": "Packaged Foods",
      "spices": "Spices & Masalas",
      "oils-ghee": "Oils & Ghee",
      "pulses": "Pulses & Legumes",
      "snacks": "Snacks",
      "beverages": "Beverages",
    };
    return editableGroceryCollection.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -50, y: -15, rotate: -3 },
      { x: 50, y: -15, rotate: 3 },
      { x: -50, y: 15, rotate: -2 },
      { x: 50, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-green-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Inter', sans-serif" }}>
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-grocery"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar - Green Theme */}
      <div className="relative bg-green-800 text-green-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Fresh Green */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-green-800/95 backdrop-blur-xl shadow-lg py-2" : "bg-green-800/80 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-green-400" : "border-green-300"}`} />
            <span className="font-bold text-xl tracking-tight text-green-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Fresh Pantry"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-green-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${activePage === item.page ? "text-green-300" : "text-green-100/80 hover:text-green-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-green-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-green-700/50">
                <FaHeart className="text-lg text-green-200 hover:text-green-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-green-800 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-green-700">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-green-700/50 text-green-200" : "text-green-100/80 hover:text-green-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {groceryHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Grocery ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-900/40 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-green-50 via-transparent to-transparent z-20 pointer-events-none" />
              
              {/* Hero edit buttons */}
              {p.previewEditMode && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-30 flex flex-wrap items-center gap-2 rounded-2xl border border-green-300/40 bg-green-900/35 px-3 py-2 backdrop-blur-sm">
                  {groceryHeroImageConfigs.map((image, index) => (
                    <button
                      key={image.field}
                      type="button"
                      onClick={(event) => handleEditHeroImage(event, image.field)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${index === activeSlide ? "bg-green-500 text-white" : "bg-white/10 text-green-100 hover:bg-white/20"}`}
                    >
                      Hero {index + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-green-800/30 backdrop-blur-md rounded-full border border-green-400/30">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-ping" />
                    <span className="text-green-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-green-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("shop", e)} 
                    className="group px-8 py-4 rounded-full bg-green-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="shop"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("offers", e)} 
                    className="px-8 py-4 rounded-full border-2 border-green-400/50 text-green-200 font-semibold tracking-wide transition-all duration-300 hover:bg-green-600/30 hover:border-green-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="offers"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-green-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO SHOP"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-green-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-green-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-0 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-green-100">
              <span className="text-green-600 text-sm">🛒</span>
              <span className="text-green-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="FRESH GROCERIES DAILY"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-green-600 text-sm">🛒</span>
            </div>
          </div>
          
          {/* Category Grid */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {groceryCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("shop", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("shop", e);
                    }
                  }}
                >
                  <div 
                    className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white"
                  >
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`cat_${cat.key}`} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Offers Banner - FIXED with InlineEditableImage */}
          <section className="w-full px-4 md:px-8 py-6">
            <div className="grid md:grid-cols-3 gap-4">
              {editableOffers.map((offer, idx) => (
                <div 
                  key={offer.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 animate-card-fly"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={(e) => openOfferModal(offer, e)}
                >
                  <div className="relative h-40">
                    <InlineEditableImage p={p} field={offer.imageField} className="block h-full w-full overflow-hidden">
                      <img src={offer.image} alt={offer.title} className="w-full h-full object-cover" />
                    </InlineEditableImage>
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${offer.color}dd, ${offer.color}99)` }} />
                    <div className="absolute inset-0 p-4 flex flex-col justify-center items-center text-white text-center">
                      <span className="text-xs bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm mb-2">
                        {offer.badge}
                      </span>
                      <h3 className="text-xl font-bold">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={offer.titleKey} 
                          value={offer.title}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="offers"
                        />
                      </h3>
                      <p className="text-sm opacity-90">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={offer.descKey} 
                          value={offer.desc}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="offers"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Products - FIXED with InlineEditableImage - REMOVED ADD TO CART */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Popular Items"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-green-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Best Sellers"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableGroceryCollection.slice(0, 4).map((item, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={item.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                      <div className="relative h-48 overflow-hidden bg-green-50">
                        <InlineEditableImage p={p} field={item.imageField} className="block h-full w-full overflow-hidden">
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {item.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: item.badgeColor }}>
                            {item.badge}
                          </div>
                        )}
                        {item.inStock && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                            In Stock
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-400">{item.brand}</span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-400">{item.weight}</span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={item.nameKey} 
                            value={item.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={item.priceKey} 
                              value={item.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {item.originalPrice > item.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{item.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {item.category}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("shop");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-green-700 text-white font-semibold hover:bg-green-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="Shop All Items"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </button>
            </div>
          </section>
          
          {/* Brands Showcase */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="brands_badge" 
                  value="Trusted Brands"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="brands"
                />
              </span>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {brands.map((brand) => (
                <div 
                  key={brand.id} 
                  className="group cursor-pointer bg-green-50 rounded-xl p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  onClick={() => setSelectedBrand(brand)}
                >
                  <div className="text-4xl mb-2">{brand.logo}</div>
                  <p className="text-sm font-semibold text-gray-700" style={{ color: brand.color }}>
                    {brand.name}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== SHOP PAGE ==================== */}
      {activePage === "shop" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_badge" 
                value="Our Store"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_title" 
                value="All Groceries"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_subtitle" 
                value="Everything you need for your kitchen, all in one place"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {groceryCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-green-700 text-white shadow-lg" 
                    : "bg-white text-gray-600 hover:bg-green-50 hover:text-green-700"
                }`}
              >
                {cat.icon} {cat.label}
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-48 overflow-hidden bg-green-50">
                    <InlineEditableImage p={p} field={item.imageField} className="block h-full w-full overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                    {item.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: item.badgeColor }}>
                        {item.badge}
                      </div>
                    )}
                    {item.inStock && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        In Stock
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">{item.brand}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">{item.weight}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={item.nameKey} 
                        value={item.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <div>
                        <span className="text-lg font-bold text-green-700">₹<InlineEditableTextWithNav 
                          p={p} 
                          field={item.priceKey} 
                          value={item.price.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="shop"
                        /></span>
                        {item.originalPrice > item.price && (
                          <span className="text-xs text-gray-400 line-through ml-1">₹{item.originalPrice}</span>
                        )}
                      </div>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {item.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🛒</div>
              <h3 className="text-xl font-semibold text-gray-600">No items found in this category</h3>
            </div>
          )}
        </section>
      )}
      
      {/* ==================== SPICES PAGE ==================== */}
      {activePage === "spices" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="spices_badge" 
                value="Spice Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="spices"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-red-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="spices_title" 
                value="Premium Spices & Masalas"
                onNavigate={handleNavigateAfterEdit}
                navPage="spices"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="spices_subtitle" 
                value="Authentic Indian spices for every dish"
                onNavigate={handleNavigateAfterEdit}
                navPage="spices"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableGroceryCollection.filter(d => d.category === "Spices & Masalas").map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-48 overflow-hidden bg-red-50">
                    <InlineEditableImage p={p} field={item.imageField} className="block h-full w-full overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                    {item.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: item.badgeColor }}>
                        {item.badge}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">{item.brand}</span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">{item.weight}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={item.nameKey} 
                        value={item.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="spices"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-red-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={item.priceKey} 
                        value={item.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="spices"
                      /></span>
                      {item.originalPrice > item.price && (
                        <span className="text-xs text-gray-400 line-through">₹{item.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== OFFERS PAGE ==================== */}
      {activePage === "offers" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-yellow-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="offers_badge" 
                value="Deals & Offers"
                onNavigate={handleNavigateAfterEdit}
                navPage="offers"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-yellow-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="offers_title" 
                value="Best Deals Today"
                onNavigate={handleNavigateAfterEdit}
                navPage="offers"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="offers_subtitle" 
                value="Save big on your grocery shopping"
                onNavigate={handleNavigateAfterEdit}
                navPage="offers"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-yellow-600 to-yellow-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {editableOffers.map((offer) => (
              <div 
                key={offer.id} 
                className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={(e) => openOfferModal(offer, e)}
              >
                <div className="relative h-64">
                  <InlineEditableImage p={p} field={offer.imageField} className="block h-full w-full overflow-hidden">
                    <img src={offer.image} alt={offer.title} className="w-full h-full object-cover" />
                  </InlineEditableImage>
                  <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${offer.color}dd, ${offer.color}99)` }} />
                  <div className="absolute inset-0 p-6 flex flex-col justify-center items-center text-white text-center">
                    <span className="text-xs bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm mb-3">
                      {offer.badge}
                    </span>
                    <h3 className="text-3xl font-bold mb-2">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={offer.titleKey} 
                        value={offer.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="offers"
                        isInModal={true}
                      />
                    </h3>
                    <p className="text-lg opacity-90">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={offer.descKey} 
                        value={offer.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="offers"
                        isInModal={true}
                      />
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== BRANDS PAGE ==================== */}
      {activePage === "brands" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="brands_page_badge" 
                value="Brands"
                onNavigate={handleNavigateAfterEdit}
                navPage="brands"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-purple-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="brands_page_title" 
                value="Trusted Brands"
                onNavigate={handleNavigateAfterEdit}
                navPage="brands"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="brands_page_subtitle" 
                value="Quality products from leading brands"
                onNavigate={handleNavigateAfterEdit}
                navPage="brands"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {brands.map((brand) => (
              <div 
                key={brand.id} 
                className="group cursor-pointer bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={() => setSelectedBrand(brand)}
              >
                <div className="text-6xl mb-3 group-hover:scale-110 transition-transform">{brand.logo}</div>
                <h3 className="font-bold text-gray-800" style={{ color: brand.color }}>
                  {brand.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1">Premium Products</p>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-green-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-green-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Food Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Tips, guides, and stories from our kitchen"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableEditorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-green-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-green-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-green-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-green-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-green-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-green-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Your Daily Grocery Partner"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-green-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Fresh Pantry was founded with a simple mission: to make quality groceries accessible to everyone. We believe that every kitchen deserves the best ingredients, delivered with care."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="From premium spices to everyday essentials, we source products from trusted brands and deliver them fresh to your doorstep. Our commitment to quality and customer satisfaction drives everything we do."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="500+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Products"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Brands"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="10k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="freshPantryAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.freshPantryAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c65d44b1-6c1c-4c6e-ad9d-acb38b761e0b_l-intro-1730486316.jpg"} 
                  alt="About Fresh Pantry" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🛒 Shop Grocery"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-green-700 text-white shadow-lg" : "text-gray-500 hover:text-green-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-green-800 to-green-900 py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-green-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Fresh Pantry Club"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-green-200 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get 15% off your first order + exclusive deals"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-green-400/30 rounded-full text-white placeholder-green-300/50 focus:border-green-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-green-600 text-white font-semibold hover:bg-green-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Item Modal - FIXED with InlineEditableImage */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedItem.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedItem(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-green-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedItem.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedItem.badgeColor }}>
                  {selectedItem.badge}
                </div>
              )}
              {selectedItem.inStock && (
                <div className="absolute bottom-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  ✅ In Stock
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-400">{selectedItem.brand}</span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">{selectedItem.weight}</span>
              </div>
              <h3 className="text-2xl font-bold text-green-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedItem.nameKey} 
                  value={selectedItem.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedItem.descriptionKey} 
                  value={selectedItem.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                  {selectedItem.category}
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {selectedItem.subCategory}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-green-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedItem.priceKey} 
                    value={selectedItem.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedItem.originalPrice > selectedItem.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedItem.originalPriceKey} 
                      value={selectedItem.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-green-600 text-center bg-green-50 p-3 rounded-full border border-green-200">
                🛒 Fresh & Quality Grocery Item
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Offer Modal - FIXED with InlineEditableImage */}
      {selectedOffer && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-lg w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-56">
              <InlineEditableImage p={p} field={selectedOffer.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedOffer.image} alt={selectedOffer.title} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${selectedOffer.color}dd, ${selectedOffer.color}99)` }} />
              <button 
                onClick={closeOfferModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-green-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full backdrop-blur-sm">
                    {selectedOffer.badge}
                  </span>
                  <h2 className="text-3xl font-bold mt-3">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedOffer.titleKey} 
                      value={selectedOffer.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="offers"
                      isInModal={true}
                    />
                  </h2>
                </div>
              </div>
            </div>
            <div className="p-6 text-center">
              <p className="text-gray-600 text-lg">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedOffer.descKey} 
                  value={selectedOffer.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="offers"
                  isInModal={true}
                />
              </p>
              <button 
                onClick={() => {
                  closeOfferModal();
                  navigateTo("shop");
                }} 
                className="mt-6 px-8 py-3 bg-green-700 text-white rounded-full font-semibold hover:bg-green-800 transition"
              >
                Shop Now
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Brand Modal */}
      {selectedBrand && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={() => setSelectedBrand(null)} 
              className="float-right text-gray-400 hover:text-gray-600 text-2xl"
            >
              ✕
            </button>
            <div className="text-7xl mb-4">{selectedBrand.logo}</div>
            <h2 className="text-2xl font-bold text-gray-800">{selectedBrand.name}</h2>
            <p className="text-gray-500 mt-2">Premium quality products you can trust</p>
            <button 
              onClick={() => {
                setSelectedBrand(null);
                navigateTo("shop");
              }} 
              className="mt-6 px-8 py-3 bg-green-700 text-white rounded-full font-semibold hover:bg-green-800 transition"
            >
              View Products
            </button>
          </div>
        </div>
      )}
      
      {/* Blog Modal - FIXED with InlineEditableImage */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-green-900">Food Stories</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-green-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-green-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-green-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-green-700 text-white hover:bg-green-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-grocery { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.1; } 50% { transform: translateY(-60px) translateX(25px); opacity: 0.3; } }
        .animate-float-grocery { animation: float-grocery 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}